<template>
  <div
    class="node"
    :class="
      [
        selected(),
        node.name,
        hasWarning ? 'warning' : '',
        hasError ? 'error' : '',
      ] | kebab
    "
  >
    <div class="title">
      <span>
        <img class="icon" src="../assets/icons/timer.svg">
        Timer
        <md-tooltip md-direction="top">A delay on a check or effect</md-tooltip>
      </span>
      <help-tooltip v-if="hasWarning" type="warning">
        <span v-for="warning in warnings" :key="warning.message">
          {{ warning.message }}
          <br>
        </span>
      </help-tooltip>
      <help-tooltip v-if="hasError" type="error">
        <span v-for="error in errors" :key="error.message">
          {{ error.message }}
          <br>
        </span>
      </help-tooltip>
      <div v-if="editor.isAdmin" class="nodeId">ID:{{ node.id }}</div>
    </div>

    <el-input
      :value="state.expiry"
      placeholder="∞"
      :controls="false"
      class="expiry-input"
      type="number"
      :disabled="state.readonly"
      @input="expiry => updateExpiry(expiry)"
    >
      <template slot="append">s</template>
    </el-input>

    <migration-warning
      v-if="migrationWarning && node.data.fromInfusionMigration"
      :warning-object="migrationWarning"
      @dismiss="dismissMigrationWarning"
    />

    <div class="sockets-container">
      <div v-for="input in inputs()" :key="input.key" class="input">
        <Socket v-socket:input="input" class="socket" type="input" :socket="input.socket" />
        <div v-show="!input.showControl()" class="input-title">{{ input.name }}</div>
        <div v-show="input.showControl()" v-control="input.control" class="input-control" />
      </div>
      <div v-for="output in outputs()" :key="output.key" class="output">
        <div class="output-title">{{ output.name }}</div>
        <Socket v-socket:output="output" class="socket" type="output" :socket="output.socket" />
      </div>
    </div>
  </div>
</template>

<script>
import ReteVueRenderPlugin from 'rete-vue-render-plugin';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import intersection from 'lodash/intersection';
import toArray from 'lodash/toArray';

import HelpTooltip from '../../common/components/HelpTooltip';
import MigrationWarning from '../controls/common/MigrationWarning';

export default {
  name: 'timer',
  components: {
    Socket: ReteVueRenderPlugin.Socket,
    HelpTooltip,
    MigrationWarning,
  },
  mixins: [ReteVueRenderPlugin.mixin],
  props: {},
  data() {
    return {
      state: {
        activeControlType: undefined,
        activeControl: null,
        hasInputConnection: true,
        hasOutputConnection: true,
        readonly: false,
      },
    };
  },
  computed: {
    hasWarning() {
      return this.warnings && this.warnings.length;
    },
    isValid() {
      return this.state.hasOutputConnection;
    },
    errors() {
      if (!this.editor.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.editor.$store.getters['validation/errorsByFieldAndId'],
          `caseLogicDescription.${this.node.id}`,
        ),
        'message',
      );
    },
    hasError() {
      return this.errors && this.errors.length;
    },
    warnings() {
      if (!this.editor.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.editor.$store.getters['validation/visibleWarningsByFieldAndId'],
          `caseLogicDescription.${this.node.id}`,
        ),
        'message',
      );
    },
    migrationWarning() {
      return this.warnings.find(warning => warning.metadata.migrationWarning);
    },
    chainableComponents() {
      return intersection(['Check', 'Effect', 'EffectChain'], toArray(this.editor.components.keys()));
    },
  },
  created() {
    this.state.expiry = this.node.data.expiry || 0;
    this.editor.on('readonly', readonly => {
      this.state.readonly = readonly;
    });
  },
  methods: {
    updateExpiry(expiry) {
      const expiryInt = parseInt(expiry, 10) || 0;
      this.state.expiry = expiryInt;
      this.node.data.expiry = expiryInt;
      this.editor.trigger('process', this.node);
      this.$analytics.event('Authoring Tool', 'timer_updated', undefined, expiryInt);
    },
    dismissMigrationWarning() {
      this.node.data.fromInfusionMigration = null;
      this.editor.trigger('process', this.node);
    },
  },
};
</script>

<style scoped>
.node {
  background: rgb(130, 130, 130);
  border-radius: 10px;
  cursor: pointer;
  min-width: 180px;
  height: auto;
  padding-bottom: 6px;
  box-sizing: content-box;
  position: relative;
  user-select: none;
  display: flex;
  flex-direction: column;
  padding: 0 5px;
  box-sizing: border-box;
}
.node:hover {
  background: rgba(130, 130, 130, 0.8);
}
.node.selected {
  box-shadow: 0 3px 6px 0 rgba(255, 255, 255, 0.7);
  border: solid 4px #ffffff;
}
.node.highlighted {
  background: #FBD430;
  border-color: #FBD430;
}
.node .icon {
  width: 16px;
  height: 16px;
  display: inline;
}
.node .title {
  color: white;
  font-family: sans-serif;
  font-size: 18px;
  padding: 8px;
}
.node .nodeId {
  float: right;
}
.sockets-container {
  display: flex;
  justify-content: space-between;
}
.node .socket.input {
  background: url("../assets/icons/active-input.svg");
  background-size: 100%;
  background-repeat: no-repeat;
  margin-left: 0;
}
.node .socket.output {
  background: url("../assets/icons/active-output.svg");
  background-size: 100%;
  background-repeat: no-repeat;
  margin-right: 0;
}
.node .socket {
  border: none;
}
.node .socket:hover {
  border: solid;
}
.node .output {
  text-align: right;
}
.node .input {
  text-align: left;
}
.node .input-title,
.node .output-title {
  vertical-align: middle;
  color: white;
  display: inline-block;
  font-family: sans-serif;
  font-size: 14px;
  margin: 6px;
  line-height: 24px;
}
.node .input-control {
  z-index: 1;
  width: calc(100% - 36px);
  vertical-align: middle;
  display: inline-block;
}
.node .control {
  padding: 6px 18px;
}
.expiry-input {
  width: 200px;
}
.node.error {
  border: solid 3px #f92c2d;
}
.node.warning {
  border: solid 3px #f2994a;
}
.migration-warning {
  margin-top: 8px;
}
</style>
